import { useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LifeBuoy, BrainCircuit, Globe2, Code2, Workflow, ShieldCheck, Plus, Check } from "lucide-react";
import { LanguageContext } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];
const ICONS = [LifeBuoy, BrainCircuit, Globe2, Code2, Workflow, ShieldCheck];
const SERVICE_IMAGES = [
  "https://customer-assets-0z36b82j.emergentagent.net/job_dadabio-tech-hub/artifacts/7tf290fw_images_02.jpg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_dadabio-tech-hub/artifacts/yum2ygkl_images_06.jpg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_dadabio-tech-hub/artifacts/71t48glo_images_07.jpg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_dadabio-tech-hub/artifacts/embinhko_images_08.jpg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_dadabio-tech-hub/artifacts/y3js9hgb_images_04.jpg",
  "https://images.unsplash.com/photo-1667372283496-893f0b1e7c16?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxjbG91ZCUyMHNlY3VyaXR5JTIwc2hpZWxkJTIwZ2xvd2luZyUyMGRhcmslMjBibHVlfGVufDB8fHx8MTc5MDYwNzQxOHww&ixlib=rb-4.1.0&q=85",
];

export default function Services() {
  const { lang, t } = useContext(LanguageContext);
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="services" data-testid="services-section" className="relative py-28 md:py-40">
      <div className="max-w-screen-xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#00F0FF] mb-6"
        >
          {t.services.label}
        </motion.p>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-display font-black tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02]"
          >
            {t.services.heading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="text-sm font-light text-zinc-500 max-w-xs"
          >
            {t.services.sub}
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i];
            const open = openIdx === i;
            return (
              <motion.div
                key={`${lang}-${s.num}`}
                data-testid={`service-card-${s.num}`}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.8, delay: (i % 2) * 0.1, ease: EASE }}
                className={`rounded-2xl border bg-[#121212] transition-[border-color,box-shadow] duration-500 overflow-hidden ${
                  open
                    ? "border-[#00F0FF]/40 shadow-[0_0_50px_rgba(0,240,255,0.08)]"
                    : "border-white/10 hover:border-white/25"
                }`}
              >
                <button
                  data-testid={`service-toggle-${s.num}`}
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full text-start p-7 md:p-8 flex items-start gap-5"
                >
                  <span className="font-mono text-xs text-[#00F0FF]/70 pt-1.5 shrink-0">{s.num}</span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-3 mb-2">
                      <Icon size={20} className={`shrink-0 transition-colors duration-500 ${open ? "text-[#00F0FF]" : "text-zinc-500"}`} />
                      <span className="font-display font-bold text-lg md:text-xl text-white leading-snug">{s.title}</span>
                    </span>
                    <span className="block text-sm font-light text-zinc-500">{s.tagline}</span>
                  </span>
                  <motion.span
                    animate={{ rotate: open ? 45 : 0 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className={`shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-colors duration-300 ${
                      open ? "border-[#00F0FF]/60 text-[#00F0FF]" : "border-white/15 text-zinc-400"
                    }`}
                  >
                    <Plus size={15} />
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="px-7 md:px-8 pb-8 pt-1 ms-[52px]">
                        <img
                          data-testid={`service-image-${s.num}`}
                          src={SERVICE_IMAGES[i]}
                          alt={s.title}
                          loading="lazy"
                          className="w-full h-44 md:h-52 object-cover rounded-xl border border-white/10 mb-6"
                        />
                        <p className="text-sm md:text-[15px] font-light text-zinc-400 leading-relaxed mb-6">{s.detail}</p>
                        <ul className="grid sm:grid-cols-2 gap-2.5">
                          {s.points.map((p) => (
                            <li key={p} className="flex items-center gap-2.5 text-[13px] text-zinc-300">
                              <Check size={13} className="text-[#00F0FF] shrink-0" />
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
